import { ref } from 'vue';
import type { Ref } from 'vue';
import type { Message, MessageMethod } from '../../../infrastructure/resources/handlers/websocket/models/chat_message';
import {
  serializeStreamDataMessage,
  serializeStreamReconnectMessage,
  serializeUserStreamEndMessage,
} from '../../../infrastructure/resources/libs/contracts/streaming';
import { useSessionStore } from '@stores/session';
import { fetchAuthSession } from "aws-amplify/auth";
import { notify } from '@components/NotificationPlugin';
import { getTranslation } from '../i18nlocal';
const messages: Ref<Message[]> = ref([]);

let connectionPromise: Promise<boolean> | null = null;
// Backoff state + pending timer so close/error and stopWebsocket cooperate
// instead of racing (double reconnect, or reconnect-after-stop).
let reconnectAttempts = 0;
let reconnectTimer: number | null = null;
// Hello pagination: the server drains the notification queue in bounded
// windows and answers {type:'message', messages:[...], hasMore:boolean}.
// While hasMore is true the backlog extends beyond this window and the
// client must send another Hello. Bounded so a stuck server flag cannot
// loop forever; the cap is logged, never silent.
let helloPage = 0;
const HELLO_MAX_PAGES = 10;
const HELLO_NEXT_PAGE_DELAY_MS = 500;

interface WebsocketService {
  websocket: WebSocket | null;
  previDToken: string;
  reconnect: boolean;
  connect(url: string, idToken: string): Promise<boolean>;
  subscribe(channel: string): void;
  emitMessage(data: Message): void;
  send(data: Message): boolean;
  sendStreamChunk(sessionId: string, chunk: ArrayBuffer | Uint8Array | string, timestamp?: number, duration?: number, sequence?: number, chunkId?: string): Promise<boolean>;
  reconnectStream(sessionId: string): Promise<boolean>;
  endStream(sessionId: string): Promise<boolean>;
  createCheckoutSession(params: CheckoutParams): Promise<boolean>;
  cancelSubscription(subscriptionId: string): Promise<boolean>;
  updateSubscription(subscriptionId: string, data:any): Promise<boolean>;
  doCommand(command: string, payload: any): Promise<boolean>;
}

interface CheckoutParams {
  type: 'subscription' | 'payment';
  planId?: string;
  amount?: number;
  credits?: number;
  description?: string;
}

async function startWebsocket(): Promise<boolean> {
  const appConfig = useSessionStore().appConfig;
  const broadcastUrl = appConfig?.broadcast_url;
  if (!appConfig?.enabled || !broadcastUrl) {
    return false;
  }
  

  // If there's already a connection attempt in progress, return that promise
  if (connectionPromise) {
    return connectionPromise;
  }

  // If websocket exists and is open, reuse it; if it is still opening, await
  // the open instead of letting the caller send on a CONNECTING socket.
  if (websocketService.websocket &&
    websocketService.websocket.readyState === WebSocket.OPEN) {
    return true;
  }
  if (websocketService.websocket &&
    websocketService.websocket.readyState === WebSocket.CONNECTING) {
    return waitForSocketOpen(websocketService.websocket);
  }

  // Create new connection promise
  connectionPromise = new Promise<boolean>((resolve) => {
    fetchAuthSession()
      .then((currentSession) => {
        if (currentSession) {
          return websocketService.connect(broadcastUrl, currentSession.tokens?.idToken?.toString() as string);
        } else {
          return Promise.reject('No current session');
        }
      })
      .then(() => {
        resolve(true);
      })
      .catch((error) => {
        resolve(false);
      })
      .finally(() => {
        // Clear the connection promise when done
        connectionPromise = null;
      });
  });

  return connectionPromise;
}

// Resolves true once the socket opens, false on close/error first. Used by
// connect() so callers never send on a still-CONNECTING socket.
function waitForSocketOpen(socket: WebSocket): Promise<boolean> {
  if (socket.readyState === WebSocket.OPEN) return Promise.resolve(true);
  if (socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const cleanup = () => {
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('close', onFail);
      socket.removeEventListener('error', onFail);
    };
    const onOpen = () => { cleanup(); resolve(true); };
    const onFail = () => { cleanup(); resolve(false); };
    socket.addEventListener('open', onOpen);
    socket.addEventListener('close', onFail);
    socket.addEventListener('error', onFail);
  });
}

// Single Hello frame builder: the initial open-Hello and every paginated
// re-Hello share this exact shape (server route matches method 'Hello').
function sendHello(): boolean {
  return websocketService.send({
    type: 'Message',
    text: 'Hello',
    method: 'Hello',
    sender: '',
    timestamp: new Date(),
    channelId: '',
    messageId: '',
    data: {}
  } as unknown as Message);
}

const websocketService: WebsocketService = {
  websocket: null,
  previDToken: '',
  reconnect: false,


  async doCommand(command: MessageMethod, payload: any): Promise<boolean> {
    try {
      const message: Message = {
        type: 'Message',
        method: command,
        data: payload,
      };

      // Ensure connection before sending
      if (!(await startWebsocket())) {
        notify('danger', getTranslation('errors.connection'));
        return false;
      }

      const sent = this.send(message);
      if (!sent) {
        notify('danger', getTranslation('errors.requestFailed', { command }));
        return false;
      }
      return true;

    } catch (error:any) {
      notify('danger', getTranslation('errors.requestFailed', { command }) + (error?.message ? ` (${error.message})` : ''));
    }
    return false;
  },

 async connect(url: string, idToken: string): Promise<boolean> {
    if (!idToken) {
      return false;
    }

    const existing = this.websocket;
    if (existing && existing.readyState !== WebSocket.CLOSED && existing.readyState !== WebSocket.CLOSING) {
      if (this.previDToken == idToken) {
        if (existing.readyState === WebSocket.OPEN) return true;
        // Same-token socket still opening: await it instead of orphaning a
        // second CONNECTING socket that would keep firing events.
        return await waitForSocketOpen(existing);
      }
      // Token changed: close the stale socket so it cannot keep delivering
      // messages or drive stray reconnects, then replace it below.
      try { existing.close(); } catch { /* closing is best-effort */ }
    }

    this.previDToken = idToken;
    // CSP/WAF note: idToken in query is logged by ALB/CF; prefer ticket exchange in future — tracked as TODO
    const socket = new WebSocket(url + `?idToken=${idToken}`);
    this.websocket = socket;
    this.reconnect = true;
    socket.addEventListener('open', () => {
      reconnectAttempts = 0;
      helloPage = 0;
      if (!sendHello()) {
        console.warn('Failed to send Hello message to the server');
      }
    });

    socket.addEventListener('message', (event) => {
      if (event.data === 'Hello from server') {
        return;
      }
      try {
        const parsedData = JSON.parse(event.data);

        this.emitMessage(parsedData);

      } catch (error) {
        useSessionStore().logMessage(event.data, 'info');
      }
    });

    socket.addEventListener('close', (event) => {
      // A replaced (stale) socket's close must not clear connectionPromise or
      // schedule a reconnect — it was deliberately closed for a newer socket.
      if (this.websocket !== socket) return;
      connectionPromise = null;
      // Reconnect with backoff, not a tight loop: the server may be
      // restarting, and an immediate reconnect storm hides real outages.
      if (this.reconnect) {
        const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(5, reconnectAttempts++)));
        reconnectTimer = window.setTimeout(() => { void startWebsocket(); }, delay);
      }
    });

    socket.addEventListener('error', (error: Event) => {
      if (this.websocket !== socket) return;
      console.error('WebSocket error:', error);
      connectionPromise = null;
      // The close event follows error — let it drive the backoff reconnect
      // instead of firing a second immediate attempt here.
    });
    // Resolve only once the socket actually opens: callers send immediately
    // after connect() returns, and a CONNECTING socket drops every send.
    return await waitForSocketOpen(socket);
  },

send(data) {
    try {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        if (data.method === 'StreamData' && data.data) {
          // Shared contract: normalize to base64 JSON StreamingParams
          // {sessionId, chunk, timestamp, duration}. Invalid chunks fail fast
          // (return false) instead of emitting empty bytes the server rejects.
          const src: any = data.data;
          const sid = (data as any).sessionId || (src && src.sessionId) || '';
          const built = serializeStreamDataMessage({
            sessionId: sid,
            chunk: src && typeof src === 'object' && 'chunk' in src ? src.chunk : src,
            timestamp: src?.timestamp,
            duration: src?.duration,
            sequence: src?.sequence,
            chunkId: src?.chunkId,
          });
          if (!built.ok) {
            console.error('WebSocket stream chunk rejected:', built.error);
            return false;
          }
          this.websocket.send(built.body);
        } else {
          this.websocket.send(JSON.stringify({ data: data }));
        }
        return true;
      }
    } catch (error) {
      console.error('WebSocket send error:', error);
    }
    return false;
  },
  async sendStreamChunk(sessionId: string, chunk: ArrayBuffer | Uint8Array | string, timestamp?: number, duration?: number, sequence?: number, chunkId?: string): Promise<boolean> {
    // Contract-coherent with the backend: the same shared serializer the
    // server validates against. Sequence (when provided) arms the server's
    // duplicate/out-of-order fence; chunks are pinned to the bound socket and
    // only reconnectStream rebinds.
    const built = serializeStreamDataMessage({ sessionId, chunk, timestamp, duration, sequence, chunkId });
    if (!built.ok) {
      console.error('WebSocket stream chunk rejected:', built.error);
      return false;
    }
    if (!(await startWebsocket())) {
      return false;
    }
    return this.send({ type: 'Message', method: 'StreamData', data: built.value } as unknown as Message);
  },
  async reconnectStream(sessionId: string): Promise<boolean> {
    const built = serializeStreamReconnectMessage(sessionId);
    if (!built.ok) {
      console.error('WebSocket stream reconnect rejected:', built.error);
      return false;
    }
    // Reconnect reuses the current socket when open; otherwise the backoff
    // reconnect re-establishes it and the caller retries on the new socket.
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        this.websocket.send(built.body);
        return true;
      } catch (error) {
        console.error('WebSocket reconnect send error:', error);
        return false;
      }
    }
    return false;
  },
  async endStream(sessionId: string): Promise<boolean> {
    const built = serializeUserStreamEndMessage(sessionId);
    if (!built.ok) {
      console.error('WebSocket stream end rejected:', built.error);
      return false;
    }
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        this.websocket.send(built.body);
        return true;
      } catch (error) {
        console.error('WebSocket end send error:', error);
        return false;
      }
    }
    return false;
  },
  subscribe(channel: string) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        action: 'subscribe',
        channel,
      };
      this.websocket.send(JSON.stringify(message));
    }
  },
  emitMessage(data: Message) {
    // Hello pagination lives here (central: every Hello reply passes through
    // this socket service, while dashboards only render the messages array).
    // hasMore:true means the server held back further backlog — re-send Hello
    // once per window, bounded, with a small delay so bursts settle.
    const helloMore = (data as unknown as { type?: unknown; hasMore?: unknown })?.hasMore === true
      && (data as unknown as { type?: unknown })?.type === 'message';
    if (helloMore) {
      if (helloPage < HELLO_MAX_PAGES) {
        helloPage += 1;
        window.setTimeout(() => {
          if (!sendHello()) {
            console.warn('Failed to send paginated Hello message to the server');
          }
        }, HELLO_NEXT_PAGE_DELAY_MS);
      } else {
        console.warn('Hello pagination hit the bounded page cap; backlog may remain until the next Hello');
      }
    }
    if (data.type === 'message' && data.messages && data.messages.length == 0) {
      return;
    }
    const event = new CustomEvent('message', {
      detail: data,
    });
    document.dispatchEvent(event);
  },
  async cancelSubscription(subscriptionId: string): Promise<boolean> {
    return this.doCommand('unsubscribe', { subscriptionId });
  },

  async updateSubscription(subscriptionId: string, data: any): Promise<boolean> {
    return this.doCommand('update', { subscriptionId, ...data });
  },

  async createCheckoutSession(params: CheckoutParams): Promise<boolean> {
    if (params.type === 'subscription') {
      return this.doCommand('subscribe', params);
    }
    return this.doCommand('payment', params);
  }
};

async function stopWebsocket() {
  try
  {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempts = 0;
    if (websocketService.websocket) {
      websocketService.reconnect = false;
      websocketService.websocket.close();
      connectionPromise = null;
     
    }
  }
  catch (error:any) {
    console.error('Failed to stop websocket', error);
  }
}


export { websocketService, stopWebsocket, messages, startWebsocket };